import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Select variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const selectTriggerVariants = cva(
  [
    // Base styles - structural styles only
    'flex w-full items-center justify-between',
    'h-11',
    'text-b5',
    'font-body font-normal',
    'rounded-sm',
    'px-3 pt-2 pb-1.5',
    'transition-colors duration-200',
    // Focus styles - visible focus ring per WCAG 2.1 AA
    'focus:outline-none',
    'focus-visible:outline-none',
    'focus-visible:ring-0',
    'focus-visible:ring-offset-0',
    // Disabled styles
    'disabled:cursor-not-allowed',
    'disabled:opacity-60',
    '[&>span]:line-clamp-1',
  ],
  {
    variants: {
      variant: {
        default: [
          // Background and border colors
          'bg-white',
          'text-body',
          'border border-neutral-200',
          // Hover effect
          'hover:border-neutral-300',
          // Focus effect
          'focus-visible:border-neutral-500',
          'focus-visible:ring-2',
          'focus-visible:ring-neutral-100',
          // Disabled colors
          'disabled:bg-neutral-100',
          'disabled:border-neutral-200',
          'disabled:text-subtext',
        ],
        'two-line': [
          // Background and border colors - same as default
          'bg-white',
          'text-body',
          'border border-neutral-200',
          // Hover effect
          'hover:border-neutral-300',
          // Focus effect
          'focus-visible:border-neutral-500',
          'focus-visible:ring-2',
          'focus-visible:ring-neutral-100',
          // Disabled colors
          'disabled:bg-neutral-100',
          'disabled:text-subtext',
          // Two-line specific: extra height for floating label
          'h-11',
          'pt-5 pb-1.5',
        ],
      },
      error: {
        true: [
          // Error border
          'border-error-500',
          'text-error-500',
          // Error focus styles
          'focus-visible:border-error-500',
          'focus-visible:ring-2',
          'focus-visible:ring-error-100',
        ],
        false: '',
      },
      hasValue: {
        true: 'text-body',
        false: 'text-subtext',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      hasValue: false,
    },
    compoundVariants: [
      {
        error: true,
        hasValue: true,
        className: 'text-error-500',
      },
    ],
  },
);

export type SelectTriggerVariantProps = VariantProps<
  typeof selectTriggerVariants
>;
